// Story 018 — fetch SVG blade outline for the current inspection's
// dominant component. The BE resolves three-tier (per-component →
// `dl_*` base-type dummy → 404). On 404 the FE renders a `<rect>`
// proxy fallback.
import { useQuery } from "@tanstack/react-query";
import { useApi } from "@aeriosiq/api-client";

export interface ComponentOutline {
  svg: string;
  source: "specific" | "base-type-dummy";
  baseType:
    | "ROTOR_BLADE"
    | "TOWER"
    | "NACELLE"
    | "FOUNDATION"
    | "SPINNER"
    | "ACCESSORIES"
    | "UNDEFINED";
  bladeLengthM: number | null;
}

export const componentOutlineKeys = {
  all: ["component-outline"] as const,
  byId: (componentId: string) =>
    [...componentOutlineKeys.all, componentId] as const,
};

export function useComponentOutlineQuery(componentId: string | null) {
  const api = useApi();
  return useQuery<ComponentOutline | null>({
    queryKey: componentOutlineKeys.byId(componentId ?? "_none"),
    enabled: componentId != null && componentId.length > 0,
    queryFn: async () => {
      try {
        const response = await api.get<ComponentOutline>(
          `/components/${encodeURIComponent(componentId ?? "")}/outline`,
        );
        return response.data;
      } catch (err) {
        // 404 → BE has no outline; FE renders fallback. Anything else
        // bubbles so React Query's standard error UI handles it.
        if (
          typeof err === "object" &&
          err != null &&
          "response" in err &&
          typeof (err as { response?: { status?: number } }).response ===
            "object" &&
          (err as { response?: { status?: number } }).response?.status === 404
        ) {
          return null;
        }
        throw err;
      }
    },
    staleTime: 5 * 60_000, // outlines are platform metadata; safe to cache
  });
}
